import React, { useMemo } from 'react';
import { Box } from '@mui/material';
import { OrganizationTreeNode } from '../../../components/StructureTable/index.types';
import {
  StyledRow,
  StyledTable,
  StyledCell,
  StyledGroupHeaderCell,
  StyledProductCellContainer,
} from '../../../components/StructureTable/index.styles';
import Row from './Row';
import { useTranslation } from 'react-i18next';
import {
  BranchTagWithCountry,
  useBranchesWithCountry,
} from 'src/utils/branches';
import { useAppSelector } from 'src/redux-file/hooks';
import CountryGroupHeader, {
  CountryGroupBulkControls,
} from '../../../components/CountryGroupHeader';
import {
  countryKeysOf,
  useCountryGroupCollapse,
} from '../../../components/CountryGroupHeader/useCountryGroupCollapse';

interface Props {
  data: OrganizationTreeNode[];
  // Selected country filter from Structure/index.tsx: '' = all countries,
  // NO_COUNTRY_VALUE = branches with no country assigned, else an ISO code.
  country: string;
}

// Mirrors the backend's own `country=none` convention (BR-2 in
// specs/.../20260826-org-tree-search-pagination.md) so the same sentinel
// drives both the API's `country` query param (Structure/index.tsx) and
// this table's client-side column grouping/narrowing.
export const NO_COUNTRY_VALUE = 'none';

interface BranchCountryGroup {
  key: string;
  label: string;
  branches: BranchTagWithCountry[];
}

// EC-3: a branch with no country is grouped under a "Bez kraju" header cell
// placed AFTER every named-country group (never interleaved with them --
// branches of the same group must be column-contiguous for the group's
// spanning header cell to make sense). Named-country groups keep the order
// in which they first appear among the (already filtered) branches.
const groupBranchesByCountry = (
  branches: BranchTagWithCountry[],
  noCountryLabel: string
): BranchCountryGroup[] => {
  const groups: BranchCountryGroup[] = [];
  const indexByCode = new Map<string, number>();
  let noCountryGroup: BranchCountryGroup | null = null;

  branches.forEach((branch) => {
    if (!branch.country_code) {
      if (!noCountryGroup) {
        noCountryGroup = {
          key: NO_COUNTRY_VALUE,
          label: noCountryLabel,
          branches: [],
        };
      }
      noCountryGroup.branches.push(branch);
      return;
    }

    let index = indexByCode.get(branch.country_code);
    if (index === undefined) {
      index = groups.length;
      indexByCode.set(branch.country_code, index);
      groups.push({
        key: branch.country_code,
        label: branch.country_name || branch.country_code,
        branches: [],
      });
    }
    groups[index].branches.push(branch);
  });

  if (noCountryGroup) groups.push(noCountryGroup);
  return groups;
};

const StructureTable = (props: Props) => {
  const { t } = useTranslation();
  const { data, country } = props;
  const { branches: allBranches } = useBranchesWithCountry();
  const user = useAppSelector((state) => state.platform.user);

  // Goal 1: column set limited to the selected country when the filter is
  // active -- narrow BEFORE grouping so a single-country selection renders
  // as one group spanning just its own branches.
  const filteredBranches = useMemo(() => {
    if (!country) return allBranches;
    if (country === NO_COUNTRY_VALUE) {
      return allBranches.filter((branch) => !branch.country_code);
    }
    return allBranches.filter((branch) => branch.country_code === country);
  }, [allBranches, country]);

  const groups = useMemo(
    () =>
      groupBranchesByCountry(
        filteredBranches,
        String(t('superadmin.settings.tabs.structure.filters.noCountry'))
      ),
    [filteredBranches, t]
  );

  // specs/001-raben-country-masteradmin/mass-specs/specs/
  // 20260826-fe-country-group-folding.md (Goal 3/5, corrected). Every group
  // starts collapsed on first visit regardless of scope -- the original
  // "expand my countries" default was wrong (it always expanded everything:
  // a scoped admin never sees a country outside their own access, and the
  // one account that sees all seventeen, MasterAdmin, has access to all of
  // them). `allBranches` is UNFILTERED by the country-Select above, so
  // `allCountryKeys` covers every country this screen could ever show --
  // used only by collapseAll()/expandAll() so a bulk action taken while
  // filtered to one country doesn't wipe out every OTHER country's recorded
  // state once the filter is cleared again.
  const allCountryKeys = useMemo(
    () => countryKeysOf(allBranches, NO_COUNTRY_VALUE),
    [allBranches]
  );

  const storageKey = `superadmin.countryFold.structure.${user?.id ?? 'anon'}`;

  const collapse = useCountryGroupCollapse({
    storageKey,
    allKeys: allCountryKeys,
  });

  // EC-6: a screen narrowed to exactly one group (typically the country
  // filter above) renders it expanded, "not collapsible into uselessness"
  // -- collapsing your only group saves no horizontal space. This is a
  // RENDER-time override only (never calls collapse.toggle/persist), so it
  // never fights an explicit collapseAll(): once collapse.isFullyCollapsed
  // is true, this defers to the real per-key state, matching "collapse-all
  // still works ... leaves the screen empty of detail, which is acceptable
  // and reversible" -- the one named exception to "expanded".
  const resolveOpen = (key: string) =>
    (groups.length === 1 && !collapse.isFullyCollapsed) || collapse.isOpen(key);

  // Single source of truth for how many "visual columns" the branch region
  // currently occupies -- consumed identically by both header rows below
  // AND by Row's cells, so a collapsed group's header width and its body
  // cell's width can never disagree (the colSpan-math bug class C13 already
  // had to fix once: BR-4/Testing Expectations).
  const totalVisualColumns = useMemo(
    () =>
      groups.reduce(
        (sum, group) =>
          sum + (resolveOpen(group.key) ? group.branches.length : 1),
        0
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [groups, collapse]
  );
  const columnWidthPct = 30 / (totalVisualColumns || 1);
  const columnWidth = `${columnWidthPct}%`;

  // Pre-resolve isOpen per group ONCE, so Row.tsx (rendered once per
  // organization) doesn't need its own collapse-state dependency -- it just
  // reads a plain boolean already computed here, from the exact same
  // `groups` the header rows below render from.
  const rowGroups = useMemo(
    () =>
      groups.map((group) => ({
        ...group,
        isOpen: resolveOpen(group.key),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [groups, collapse]
  );

  return (
    <StyledTable>
      <Box sx={{ display: 'flex', mb: '12px' }}>
        <CountryGroupBulkControls
          collapseAllLabel={String(
            t('superadmin.settings.countryFolding.bulk.collapseAll')
          )}
          expandAllLabel={String(
            t('superadmin.settings.countryFolding.bulk.expandAll')
          )}
          onCollapseAll={collapse.collapseAll}
          onExpandAll={collapse.expandAll}
        />
      </Box>
      <StyledRow>
        {/* Blank filler over the columns that visually "span" both header
            rows (unit name / products / select) -- same header background,
            label lives on the row below since this layout has no real
            <table> rowSpan to center it in. */}
        <StyledCell type="header" w="40%" sx={{ borderLeftColor: '#38414f' }} />
        {groups.map((group) => {
          const isOpen = resolveOpen(group.key);
          const span = isOpen ? group.branches.length : 1;
          return (
            <StyledGroupHeaderCell
              key={group.key}
              w={`${columnWidthPct * span}%`}
              data-testid="country-group-header"
              data-branch-count={group.branches.length}
              sx={{ p: 0 }}
            >
              <CountryGroupHeader
                label={group.label}
                isOpen={isOpen}
                onToggle={() => collapse.toggle(group.key)}
                actionLabel={String(
                  t(
                    isOpen
                      ? 'superadmin.settings.countryFolding.collapseGroup'
                      : 'superadmin.settings.countryFolding.expandGroup',
                    { name: group.label, count: group.branches.length }
                  )
                )}
              />
            </StyledGroupHeaderCell>
          );
        })}
        <StyledCell type="header" w="20%" />
        <StyledCell type="header" w="10%" />
      </StyledRow>
      <StyledRow>
        <StyledCell type="header" w="40%" sx={{ borderLeftColor: '#38414f' }}>
          {t('superadmin.settings.tabs.structure.table.unitName')}
        </StyledCell>
        {groups.map((group) => {
          const isOpen = resolveOpen(group.key);
          if (isOpen) {
            return group.branches.map((branch) => (
              <StyledCell key={branch.name} type="header" w={columnWidth}>
                {branch.description}
              </StyledCell>
            ));
          }
          return (
            <StyledCell
              key={group.key}
              type="header"
              w={columnWidth}
              sx={{ opacity: 0.7, fontStyle: 'italic' }}
            >
              {t('superadmin.settings.countryFolding.collapsedPlaceholder')}
            </StyledCell>
          );
        })}
        <StyledCell type="header" w="20%">
          <StyledProductCellContainer>
            <span>GHG</span>
            <span>ESG</span>
            <span>LCA</span>
          </StyledProductCellContainer>
        </StyledCell>
        <StyledCell type="header" w="10%" sx={{ textAlign: 'center' }}>
          {t('superadmin.settings.tabs.structure.table.select')}
        </StyledCell>
      </StyledRow>
      {data.map((organization) => (
        <Row
          key={organization.id}
          organization={organization}
          groups={rowGroups}
          columnWidth={columnWidth}
        />
      ))}
    </StyledTable>
  );
};

export default StructureTable;
